import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/Header";
import { Sidebar } from "@/components/Sidebar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import axiosInstance from "@/config/axiosInstance";
import { endpoints } from "@/config/apiEndpoints";
import { useAdminAuth } from "@/hooks/use-admin-auth";
import { AdminAccessState } from "@/components/AdminAccessState";
import {
  Search, RefreshCw, Users, CheckCircle2, XCircle,
  ChevronLeft, ChevronRight, UserMinus,
} from "lucide-react";
import { useTimezone } from "@/contexts/TimezoneContext";

/* ===================== TYPES ===================== */

interface AgentRecord {
  id: string;
  record_id: number;
  source: "Agent DB" | "Custom Agents";
  name: string;
  email: string;
  phone: string;
  company: string;
  mail_status: "active" | "unsubscribed";
  unsubscribed_at: string | null;
  last_mailed_at: string | null;
}

interface AgentStats {
  total: number;
  subscribed: number;
  unsubscribed: number;
}

/* ===================== HELPERS ===================== */

const Skeleton = ({ className }: { className?: string }) => (
  <div className={`animate-pulse bg-gray-200 rounded ${className}`} />
);

const SkeletonRow = () => (
  <tr className="border-b border-gray-100">
    <td className="px-5 py-4"><Skeleton className="h-3 w-36" /></td>
    <td className="px-5 py-4"><Skeleton className="h-3 w-44" /></td>
    <td className="px-5 py-4"><Skeleton className="h-3 w-28" /></td>
    <td className="px-5 py-4"><Skeleton className="h-3 w-36" /></td>
    <td className="px-5 py-4"><Skeleton className="h-5 w-20 rounded-full" /></td>
    <td className="px-5 py-4"><Skeleton className="h-5 w-20 rounded-full" /></td>
    <td className="px-5 py-4"><Skeleton className="h-3 w-36" /></td>
    <td className="px-5 py-4"><Skeleton className="h-7 w-20 rounded-lg" /></td>
  </tr>
);

const StatCard = ({
  label, value, icon, valueClass, loading,
}: {
  label: string; value: number; icon: React.ReactNode;
  valueClass: string; loading: boolean;
}) => (
  <div className="bg-white rounded-2xl border border-gray-300 px-5 py-4 flex items-center gap-4 shadow-sm hover:shadow-md transition-shadow">
    <div className="p-2.5 rounded-xl bg-gray-50 flex-shrink-0">{icon}</div>
    <div>
      <p className="text-xs text-gray-400 mb-1 font-medium">{label}</p>
      {loading
        ? <Skeleton className="h-7 w-12" />
        : <p className={`text-2xl font-bold leading-none ${valueClass}`}>{value}</p>
      }
    </div>
  </div>
);

const AVATAR_COLORS = [
  { bg: "bg-blue-100", text: "text-blue-700" },
  { bg: "bg-violet-100", text: "text-violet-700" },
  { bg: "bg-emerald-100", text: "text-emerald-700" },
  { bg: "bg-rose-100", text: "text-rose-700" },
  { bg: "bg-amber-100", text: "text-amber-700" },
  { bg: "bg-cyan-100", text: "text-cyan-700" },
];
const avatarColor = (seed: string) => AVATAR_COLORS[seed.charCodeAt(0) % AVATAR_COLORS.length];

const getPaginationItems = (current: number, total: number): Array<number | "..."> => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const items: Array<number | "..."> = [];
  const ws = Math.max(2, current - 1);
  const we = Math.min(total - 1, current + 1);
  items.push(1);
  if (ws > 2) items.push("...");
  for (let p = ws; p <= we; p++) items.push(p);
  if (we < total - 1) items.push("...");
  items.push(total);
  return items;
};

/* ===================== COMPONENT ===================== */

const AdminAgents: React.FC = () => {
  const navigate = useNavigate();
  const { formatDateTime } = useTimezone();
  const toggleMobileRef = useRef<(() => void) | null>(null);
  const { status: authStatus, retrying, retry } = useAdminAuth();
  const [agents, setAgents] = useState<AgentRecord[]>([]);
  const [stats, setStats] = useState<AgentStats>({ total: 0, subscribed: 0, unsubscribed: 0 });
  const [loading, setLoading] = useState(true);
  const [filtering, setFiltering] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [mailStatus, setMailStatus] = useState("unsubscribed");
  const [dateRange, setDateRange] = useState("all_time");

  const [refreshKey, setRefreshKey] = useState(0);
  const isFirstLoad = useRef(true);
  const isRefreshTriggered = useRef(false);
  const refreshStartTime = useRef<number | null>(null);
  const refreshTimeoutRef = useRef<number | null>(null);

  const [confirmTarget, setConfirmTarget] = useState<AgentRecord | null>(null);
  const [unsubscribing, setUnsubscribing] = useState<string | null>(null);

  /* ── debounce search ── */
  useEffect(() => {
    const t = setTimeout(() => { setSearchTerm(searchInput); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  /* ── fetch ── */
  useEffect(() => {
    if (authStatus !== "admin") return;
    const isInitial = isFirstLoad.current;
    if (isInitial) setLoading(true);
    else if (isRefreshTriggered.current) setRefreshing(true);
    else setFiltering(true);

    const controller = new AbortController();
    const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
    if (searchTerm.trim()) params.set("search", searchTerm.trim());
    if (mailStatus !== "all") params.set("mail_status", mailStatus);
    if (dateRange !== "all_time") params.set("date_range", dateRange);

    axiosInstance.get(`${endpoints.admin_agents}?${params.toString()}`, { signal: controller.signal })
      .then(res => {
        const data = res.data;
        setAgents(data.results || []);
        setTotalCount(data.count || 0);
        setStats(data.stats || { total: 0, subscribed: 0, unsubscribed: 0 });
      })
      .catch(err => {
        if (err.name === "CanceledError" || err.code === "ERR_CANCELED") return;
        setAgents([]); setTotalCount(0);
        setStats({ total: 0, subscribed: 0, unsubscribed: 0 });
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        setLoading(false);
        setFiltering(false);
        if (isRefreshTriggered.current && refreshStartTime.current !== null) {
          const elapsed = Date.now() - refreshStartTime.current;
          const remaining = Math.max(0, 1200 - elapsed);
          if (remaining > 0) {
            refreshTimeoutRef.current = window.setTimeout(() => {
              setRefreshing(false);
              refreshTimeoutRef.current = null;
              refreshStartTime.current = null;
            }, remaining);
          } else {
            setRefreshing(false);
            refreshStartTime.current = null;
          }
        } else {
          setRefreshing(false);
          refreshStartTime.current = null;
        }
        isRefreshTriggered.current = false;
        isFirstLoad.current = false;
      });

    return () => {
      controller.abort();
      if (refreshTimeoutRef.current) {
        window.clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
    };
  }, [authStatus, page, pageSize, searchTerm, mailStatus, dateRange, refreshKey]);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const handleUnsubscribeConfirm = async () => {
    if (!confirmTarget) return;
    const target = confirmTarget;
    setConfirmTarget(null);
    setUnsubscribing(target.id);
    try {
      const res = await axiosInstance.post(endpoints.admin_agents_unsubscribe, {
        source: target.source,
        record_id: target.record_id,
      });
      const unsubscribed_at: string = res.data.unsubscribed_at || new Date().toISOString();
      setAgents(prev =>
        prev.map(a =>
          a.id === target.id
            ? { ...a, mail_status: "unsubscribed", unsubscribed_at }
            : a
        )
      );
      setStats(prev => ({
        ...prev,
        subscribed: Math.max(0, prev.subscribed - 1),
        unsubscribed: prev.unsubscribed + 1,
      }));
    } catch {
      // silently ignore — row stays as-is
    } finally {
      setUnsubscribing(null);
    }
  };

  /* ── loading / auth gates ── */
  if (authStatus === "loading") {
    return <AdminAgentsSkeleton />;
  }

  // if (authStatus === "unavailable") {
  //   return (
  //     <AdminAccessState
  //       pageTitle="Agent Management"
  //       variant="unavailable"
  //       onRetry={retry}
  //       retrying={retrying}
  //     />
  //   );
  // }

  // if (authStatus === "denied") {
  //   return (
  //     <AdminAccessState
  //       pageTitle="Agent Management"
  //       variant="denied"
  //     />
  //   );
  // }

  const COLS = [
    { label: "Name", width: 200 },
    { label: "Email", width: 240 },
    { label: "Phone", width: 160 },
    { label: "Company", width: 200 },
    { label: "Source", width: 130 },
    { label: "Status", width: 130 },
    { label: "Unsubscribed At", width: 200 },
    { label: "Action", width: 130 },
  ];

  /* ===================== RENDER ===================== */
  return (
    <div className="flex h-screen overflow-hidden bg-[#f8f9fb]">
      <Sidebar registerMobileToggle={(fn) => { toggleMobileRef.current = fn; }} />

      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        <PageHeader
          pageTitle="Agent Management"
          onMenuClick={() => toggleMobileRef.current?.()}
        />

        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          <div className="px-7 py-6 space-y-6 max-w-[1600px] mx-auto">

            {/* ── HEADER ── */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight">Agent Management</h1>
                <p className="text-sm text-gray-500 mt-0.5">All agents from Agent DB and Agent Users</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="h-9 gap-2 text-xs font-medium border-gray-200 hover:bg-gray-50"
                onClick={() => {
                  if (refreshing) return;
                  isRefreshTriggered.current = true;
                  refreshStartTime.current = Date.now();
                  if (refreshTimeoutRef.current) {
                    window.clearTimeout(refreshTimeoutRef.current);
                    refreshTimeoutRef.current = null;
                  }
                  setRefreshing(true);
                  setRefreshKey(k => k + 1);
                }}
                disabled={refreshing}
              >
                <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh
              </Button>
            </div>

            {/* ── STATS ── */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <StatCard label="Total Agents" value={stats.total} loading={loading}
                valueClass="text-gray-900" icon={<Users className="w-5 h-5 text-gray-500" />} />
              <StatCard label="Default" value={stats.subscribed} loading={loading}
                valueClass="text-emerald-600" icon={<CheckCircle2 className="w-5 h-5 text-emerald-500" />} />
              <StatCard label="Unsubscribed" value={stats.unsubscribed} loading={loading}
                valueClass="text-red-500" icon={<XCircle className="w-5 h-5 text-red-400" />} />
            </div>

            {/* ── FILTERS ── */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-5 py-4">
              <div className="flex flex-col gap-3">
                <div className="relative w-full">
                  <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search by name or email…"
                    value={searchInput}
                    onChange={e => setSearchInput(e.target.value)}
                    className="pl-10 h-9 text-sm border-gray-200 bg-gray-50 rounded-xl focus:bg-white focus:border-blue-400 focus:ring-2 focus:ring-blue-50 transition-all w-full"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Select value={mailStatus} onValueChange={v => { setMailStatus(v); setPage(1); }}>
                    <SelectTrigger className="h-9 text-sm border-gray-200 bg-gray-50 rounded-xl w-full">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Default</SelectItem>
                      <SelectItem value="unsubscribed">Unsubscribed</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={dateRange} onValueChange={v => { setDateRange(v); setPage(1); }}>
                    <SelectTrigger className="h-9 text-sm border-gray-200 bg-gray-50 rounded-xl w-full">
                      <SelectValue placeholder="Unsubscribed date" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all_time">All time</SelectItem>
                      <SelectItem value="24_hours">Last 24 hours</SelectItem>
                      <SelectItem value="1_week">Last 1 week</SelectItem>
                      <SelectItem value="1_month">Last 1 month</SelectItem>
                      <SelectItem value="6_months">Last 6 months</SelectItem>
                      <SelectItem value="1_year">Last 1 year</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* ── TABLE ── */}
            <div className="relative">
              <div
                className={`overflow-x-auto transition-opacity duration-200 ${filtering ? "opacity-40" : "opacity-100"}`}
                style={{ transform: "rotateX(180deg)" }}
              >
                <table
                  style={{
                    minWidth: `${COLS.reduce((a, c) => a + c.width, 0)}px`,
                    borderCollapse: "collapse",
                    width: "100%",
                    transform: "rotateX(180deg)",
                  }}
                  className="border-l border-r border-gray-300"
                >
                  <colgroup>
                    {COLS.map((c, i) => <col key={i} style={{ width: c.width }} />)}
                  </colgroup>

                  <thead>
                    <tr className="border-t border-b border-gray-300 bg-gray-50/80">
                      {COLS.map(c => (
                        <th key={c.label} className="px-5 py-3.5 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-widest whitespace-nowrap">
                          {c.label}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      Array.from({ length: pageSize }).map((_, i) => <SkeletonRow key={i} />)
                    ) : agents.length === 0 ? (
                      <tr>
                        <td colSpan={COLS.length} className="py-20 text-center">
                          <div className="flex flex-col items-center gap-3 text-gray-400">
                            <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center">
                              <Users className="w-7 h-7 opacity-40" />
                            </div>
                            <p className="text-sm font-medium">No agents match current filters</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      agents.map(agent => {
                        const isUnsub = agent.mail_status === "unsubscribed";
                        const av = avatarColor(agent.email);
                        const initials = agent.name
                          .split(" ").slice(0, 2).map(w => w[0]?.toUpperCase() || "").join("");

                        return (
                          <tr
                            key={agent.id}
                            className={`border-b border-gray-50 hover:bg-blue-50/30 transition-colors border-l-[3px] ${isUnsub ? "border-l-red-400" : "border-l-emerald-400"}`}
                          >
                            {/* Name */}
                            <td className="px-5 py-3.5 whitespace-nowrap">
                              <div className="flex items-center gap-3">
                                <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${av.bg} ${av.text}`}>
                                  {initials || "?"}
                                </div>
                                <span className="text-sm font-semibold text-gray-900">{agent.name || "—"}</span>
                              </div>
                            </td>

                            {/* Email */}
                            <td className="px-5 py-3.5 whitespace-nowrap">
                              <span className="text-sm text-gray-500">{agent.email || "—"}</span>
                            </td>

                            {/* Phone */}
                            <td className="px-5 py-3.5 whitespace-nowrap">
                              <span className="text-sm text-gray-500">{agent.phone || "—"}</span>
                            </td>

                            {/* Company */}
                            <td className="px-5 py-3.5 whitespace-nowrap">
                              <span className="text-sm text-gray-500">{agent.company || "—"}</span>
                            </td>

                            {/* Source */}
                            <td className="px-5 py-3.5 whitespace-nowrap">
                              <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full ${agent.source === "Agent DB" ? "bg-violet-50 text-violet-700" : "bg-blue-50 text-blue-700"}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${agent.source === "Agent DB" ? "bg-violet-400" : "bg-blue-400"}`} />
                                {agent.source === "Agent DB" ? "Agent DB" : "Custom Agents"}
                              </span>
                            </td>

                            {/* Status */}
                            <td className="px-5 py-3.5 whitespace-nowrap">
                              {isUnsub ? (
                                <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold">
                                  <span className="w-2 h-2 rounded-full bg-red-400" />
                                  <span className="text-red-600">Unsubscribed</span>
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold">
                                  <span className="w-2 h-2 rounded-full bg-emerald-500" />
                                  <span className="text-emerald-700">Default</span>
                                </span>
                              )}
                            </td>

                            {/* Unsubscribed At */}
                            <td className="px-5 py-3.5 whitespace-nowrap">
                              <span className="text-sm text-gray-500">
                                {agent.unsubscribed_at ? formatDateTime(agent.unsubscribed_at) : "—"}
                              </span>
                            </td>

                            {/* Action */}
                            <td className="px-5 py-3.5 whitespace-nowrap">
                              {!isUnsub && (
                                <button
                                  onClick={() => setConfirmTarget(agent)}
                                  disabled={unsubscribing === agent.id}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <UserMinus className="w-3.5 h-3.5" />
                                  {unsubscribing === agent.id ? "..." : "Unsubscribe"}
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* ── PAGINATION ── */}
              <div className="flex items-center justify-between px-5 py-4 border-t border-gray-100">
                <p className="text-xs text-gray-400 font-medium">
                  Page {page} of {totalPages} · <span className="text-gray-600">{agents.length} agents</span>
                </p>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={loading || page <= 1}
                    className="h-8 w-8 rounded-lg flex items-center justify-center border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>

                  {getPaginationItems(page, totalPages).map((item, idx) =>
                    item === "..." ? (
                      <span key={`e-${idx}`} className="w-8 text-center text-xs text-gray-400">…</span>
                    ) : (
                      <button
                        key={item}
                        onClick={() => setPage(item as number)}
                        disabled={loading}
                        className={`h-8 min-w-8 px-2 rounded-lg text-xs font-semibold transition-colors ${item === page
                          ? "bg-blue-600 text-white shadow-sm shadow-blue-200"
                          : "border border-gray-200 text-gray-600 hover:bg-gray-50"
                          }`}
                      >
                        {item}
                      </button>
                    )
                  )}

                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={loading || page >= totalPages}
                    className="h-8 w-8 rounded-lg flex items-center justify-center border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ── Unsubscribe confirm dialog ── */}
      <AlertDialog open={!!confirmTarget} onOpenChange={open => { if (!open) setConfirmTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <UserMinus className="w-5 h-5 text-red-500" />
              Unsubscribe Agent
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to unsubscribe{" "}
              <strong className="text-gray-900">{confirmTarget?.name || confirmTarget?.email}</strong>?
              They will no longer receive emails. This action cannot be undone from here.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConfirmTarget(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleUnsubscribeConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              Yes, Unsubscribe
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export const AdminAgentsSkeleton: React.FC = () => (
  <div className="flex h-screen overflow-hidden bg-[#f8f9fb]">
    <Sidebar />
    <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
      <PageHeader pageTitle="Agent Management" />
      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        <div className="px-7 py-6 space-y-6 max-w-[1600px] mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-4 w-64 mt-1.5" />
            </div>
            <Skeleton className="h-9 w-20" />
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
          </div>

          {/* Filters */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-5 py-4">
            <div className="flex flex-col gap-3">
              <Skeleton className="h-9 w-full" />
              <div className="grid grid-cols-2 gap-3">
                <Skeleton className="h-9 w-full" />
                <Skeleton className="h-9 w-full" />
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="overflow-x-auto" style={{ transform: "rotateX(180deg)" }}>
              <table className="min-w-full w-full border-collapse" style={{ transform: "rotateX(180deg)", minWidth: "1350px" }}>
                <thead className="border-b border-gray-100 bg-gray-50/80">
                  <tr>
                    {Array.from({ length: 8 }).map((_, idx) => (
                      <th key={idx} className="px-5 py-3.5 text-left">
                        <Skeleton className="h-3 w-24" />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: 6 }).map((_, idx) => <SkeletonRow key={idx} />)}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default AdminAgents;
